import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCheck,
  X,
  Mail,
  MessageSquare,
  Activity,
  AlertCircle,
  UserPlus,
  UserMinus,
  Trash2,
} from 'lucide-react';
import { ApiService } from '../../services/ApiService.ts';
import { useTranslation } from '../../i18n.ts';
import { useFocusTrap } from '../../hooks/useFocusTrap.ts';
import { useProjectStore } from '../../stores/useProjectStore.ts';
import { useUIStore } from '../../stores/useUIStore.ts';
import { useSessionStore } from '../../stores/useSessionStore.ts';
import { getNotificationRoute } from '../../utils/notificationRoute.ts';
import InvitationActions from './InvitationActions.tsx';

interface Notification {
  id: string;
  account_id: string;
  project_id: string | null;
  type: string;
  title: string;
  message: string;
  link: string | null;
  data: any;
  read_at: string | null;
  created_at: string;
}

function getTimeAgo(dateStr: string, lang: string, t: (key: string) => string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return t('justNow');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${t('minutesAgo')}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${t('hoursAgo')}`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ${t('daysAgo')}`;
  const locale = lang === 'nl' ? 'nl-NL' : 'en-GB';
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

function getNotificationIcon(type: string): React.ReactNode {
  switch (type) {
    case 'member_joined':
      return <UserPlus size={14} className="text-emerald-500" />;
    case 'member_removed':
      return <UserMinus size={14} className="text-pt-ruby dark:text-pt-ruby-light" />;
    case 'member_left':
      return <UserMinus size={14} className="text-pt-sage-text" />;
    case 'invitation_received':
    case 'invitation_accepted':
      return <Mail size={14} className="text-pt-gold-text dark:text-[var(--gold-text)]" />;
    case 'mention':
    case 'comment_reply':
      return <MessageSquare size={14} className="text-blue-500" />;
    case 'project_activity':
      return <Activity size={14} className="text-emerald-500" />;
    case 'system':
      return <AlertCircle size={14} className="text-pt-sage-text" />;
    case 'project_due_soon':
      return <AlertCircle size={14} className="text-amber-500" />;
    case 'project_overdue':
      return <AlertCircle size={14} className="text-pt-ruby dark:text-pt-ruby-light" />;
    default:
      return <Bell size={14} className="text-pt-sage-text" />;
  }
}

const NotificationBell: React.FC = () => {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const focusTrapRef = useFocusTrap(isOpen);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const fetchNotifications = useCallback(async () => {
    try {
      const { notifications: data } = await ApiService.getNotifications();
      setNotifications(data);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  // Initial fetch + polling every 60s + real-time WebSocket push
  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, 60000);
    const handleWsNotification = () => fetchNotifications();
    window.addEventListener('ws-notification', handleWsNotification);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('ws-notification', handleWsNotification);
    };
  }, [fetchNotifications]);

  // Close dropdown when clicking outside (both the trigger button and the fixed panel)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideButton = buttonRef.current && buttonRef.current.contains(target);
      const insidePanel = focusTrapRef.current && focusTrapRef.current.contains(target);
      if (!insideButton && !insidePanel) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkRead = async (id: string) => {
    try {
      await ApiService.markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    if (unreadCount === 0) return;
    setLoading(true);
    try {
      await ApiService.markAllNotificationsRead();
      setNotifications((prev) =>
        prev.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() }))
      );
    } catch (err) {
      console.error('Failed to mark all notifications read:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await ApiService.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const toggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const panelHeight = 400;
      const panelWidth = 320;
      let top = rect.top + rect.height / 2 - panelHeight / 2;
      let left = rect.right + 8;

      // Keep within viewport bounds
      if (top + panelHeight > window.innerHeight - 8) {
        top = window.innerHeight - panelHeight - 8;
      }
      if (top < 8) top = 8;
      if (left + panelWidth > window.innerWidth - 8) {
        left = rect.left - panelWidth - 8;
      }

      setDropdownPos({ top, left });
    }
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className="relative flex items-center justify-center w-8 h-8 rounded-lg text-pt-sage/60 hover:text-white hover:bg-white/10 transition-all"
        title={t('notifications')}
        aria-label={t('notifications')}
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full flex items-center justify-center px-0.5 text-[8px] font-black bg-pt-gold text-pt-forest">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel — fixed positioning to escape sidebar overflow:hidden */}
      {isOpen && dropdownPos && (
        <div
          ref={focusTrapRef}
          role="dialog"
          aria-modal="true"
          aria-label={t('notifications')}
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: '320px',
          }}
          className="bg-white dark:bg-[#141F1A] rounded-2xl shadow-2xl border border-black/5 dark:border-white/10 overflow-hidden z-[200] animate-in fade-in slide-in-from-left-2 duration-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-black/5 dark:border-white/10">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-pt-forest dark:text-pt-cream">
                {t('notifications')}
              </span>
              {unreadCount > 0 && (
                <span className="text-[9px] font-bold text-pt-gold bg-pt-gold/10 px-1.5 py-0.5 rounded-full">
                  {unreadCount} {t('unreadNotifications')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  disabled={loading}
                  className="text-[9px] font-bold text-pt-forest/50 dark:text-pt-cream/50 hover:text-pt-forest dark:hover:text-pt-cream transition-colors px-2 py-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-1"
                  title={t('markAllAsRead')}
                >
                  <CheckCheck size={12} />
                  {t('markAllAsRead')}
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-pt-forest/30 dark:text-pt-cream/30 hover:text-pt-forest dark:hover:text-pt-cream transition-colors p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Notification list */}
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4">
                <Bell size={28} className="text-black/10 dark:text-white/10 mb-2" />
                <p className="text-xs text-black/30 dark:text-white/30 font-medium">
                  {t('noNotifications')}
                </p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={async () => {
                    if (!notification.read_at) handleMarkRead(notification.id);

                    // #499 — uitnodiging routeren naar ProjectSwitcher: daar staat
                    // al een "Pending Invitations" sectie met Accept/Decline knoppen.
                    // De pendingInvitations lijst eerst verversen zodat de net-
                    // binnengekomen uitnodiging zeker zichtbaar is.
                    if (notification.type === 'invitation_received') {
                      try {
                        const invs = await ApiService.getPendingInvitations();
                        useSessionStore.getState().setPendingInvitations(invs);
                      } catch {
                        /* best effort — modal toont wat al in de store staat */
                      }
                      useUIStore.getState().setShowProjectSwitcher(true);
                      setIsOpen(false);
                      return;
                    }

                    const route = getNotificationRoute(notification);
                    const targetProjectId = notification.data?.projectId;
                    const currentProjectId = useProjectStore.getState().currentProject?.id;

                    // If the notification points at a different project, defer to
                    // App.tsx (via custom event) so it can switch project + reload
                    // people/relationships before the route changes. Refs #614.
                    if (targetProjectId && targetProjectId !== currentProjectId) {
                      window.dispatchEvent(
                        new CustomEvent('pecktree:notification-navigate', {
                          detail: { projectId: targetProjectId, route },
                        })
                      );
                    } else {
                      navigate(route);
                    }
                    setIsOpen(false);
                  }}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-black/5 dark:border-white/5 last:border-b-0 transition-colors cursor-pointer ${
                    notification.read_at
                      ? 'bg-white dark:bg-transparent hover:bg-black/[0.02] dark:hover:bg-white/[0.03]'
                      : 'bg-pt-gold/[0.04] dark:bg-pt-gold/[0.06] hover:bg-pt-gold/[0.08] dark:hover:bg-pt-gold/[0.10]'
                  }`}
                >
                  {/* Icon */}
                  <span className="mt-0.5 shrink-0 w-6 flex items-center justify-center">
                    {getNotificationIcon(notification.type)}
                  </span>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p
                        className={`text-xs font-bold truncate ${
                          notification.read_at
                            ? 'text-pt-forest/50 dark:text-pt-cream/40'
                            : 'text-pt-forest dark:text-pt-cream'
                        }`}
                      >
                        {notification.title}
                      </p>
                      {!notification.read_at && (
                        <span className="w-1.5 h-1.5 rounded-full bg-pt-gold shrink-0" />
                      )}
                    </div>
                    <p
                      className={`text-[11px] mt-0.5 leading-snug line-clamp-2 ${
                        notification.read_at
                          ? 'text-black/30 dark:text-white/25'
                          : 'text-black/50 dark:text-white/50'
                      }`}
                    >
                      {notification.message}
                    </p>
                    <p className="text-[9px] text-black/25 dark:text-white/20 mt-1 font-medium">
                      {getTimeAgo(notification.created_at, lang, t)}
                    </p>
                    {/* #499 — directe Accept/Decline knoppen voor uitnodigingen */}
                    {notification.type === 'invitation_received' && (
                      <InvitationActions
                        notification={notification}
                        size="sm"
                        onHandled={() => {
                          handleMarkRead(notification.id);
                          fetchNotifications();
                        }}
                      />
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="shrink-0 mt-1 flex items-center gap-0.5">
                    {!notification.read_at && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkRead(notification.id);
                        }}
                        className="text-pt-forest/20 dark:text-pt-cream/20 hover:text-pt-forest dark:hover:text-pt-cream transition-colors p-1 rounded hover:bg-black/5 dark:hover:bg-white/5"
                        title={t('markAsRead')}
                      >
                        <Check size={12} />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(notification.id);
                      }}
                      className="text-pt-forest/15 dark:text-pt-cream/15 hover:text-pt-ruby dark:hover:text-pt-ruby-light transition-colors p-1 rounded hover:bg-pt-ruby/5 dark:hover:bg-pt-ruby-light/5"
                      title={t('deleteNotification')}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
